import { z } from "zod";

// Signing in. Credentials are checked for real (a salted hash per account,
// ADR-004 §1) and so is the session that follows (ADR-004 §2): a row the
// server keeps, named by a signed, HttpOnly cookie.

export const impersonatingSchema = z.object({
  actorUserId: z.string(),
  actorName: z.string(),
});
export type Impersonating = z.infer<typeof impersonatingSchema>;

export const sessionSchema = z.object({
  signedIn: z.boolean(),
  userId: z.string().nullable(),
  email: z.string().nullable(),
  emailVerified: z.boolean(),
  /** The caller's live permission set, including any staff grant (S9.01): a plain passthrough
   * of `caller.permissions` (`@amtana/api`), so a navigation entry can require one
   * (`NavigationEntry.requiresPermission`, `@amtana/browser`) without a door of its own.
   * Empty while signed out. */
  permissions: z.array(z.string()),
  /** Set while this session is mid impersonation visit (S9.06, ADR-005 point 6): `userId` and
   * `email` above already name the person being viewed as, with a viewer's permissions; this
   * names who started the visit, for the shell's banner. Null for an ordinary session. */
  impersonating: impersonatingSchema.nullable(),
  /** The sign-up form's challenge key (ADR-025 §8): Cloudflare Turnstile's public site key, or
   * null while the challenge is off. The sign-up screen asks who is signed in anyway. */
  signUpSiteKey: z.string().nullable().optional(),
});
export type AuthSession = z.infer<typeof sessionSchema>;

export const signInInputSchema = z.object({
  email: z.email("Please enter a valid email address."),
  password: z.string().min(1, "Your password is invalid. Please try again."),
});
export type SignInInput = z.infer<typeof signInInputSchema>;

/**
 * The one password strength rule, used wherever a password is set: signing
 * up, resetting a forgotten one (ADR-004 §3) and changing a known one.
 */
export const passwordSchema = z.string().min(8, "Your password must be at least 8 characters long.");

// Restated from account's own `languageSchema` (contracts stay on zod and @amtana/module
// only, the same reason organization restates the role list): the language the sign-in
// switcher had active (S6.04) when the sign-up form was submitted.
const signUpLanguageSchema = z.enum(["en", "es", "pt", "it", "fr", "de"]);

// A sign-up's first touch (ADR-027 point 11, S31.09): the campaign tags, referrer and landing
// path the register screen read off its own URL, carried on to growth exactly like
// `referralToken` is, never checked here, since a bad or missing one must never refuse the
// sign-up. Restated from growth's own `firstTouchInputSchema` (a contract depends only on zod
// and @amtana/module, D017's reason).
export const firstTouchInputSchema = z.object({
  source: z.string().optional(),
  medium: z.string().optional(),
  campaign: z.string().optional(),
  content: z.string().optional(),
  term: z.string().optional(),
  referrer: z.string().optional(),
  landingPath: z.string().optional(),
  /** The website's PostHog visitor id, forwarded only after the visitor allowed analytics
   * (ADR-028 revision 1 point 4, S33.15); growth keeps it on the first touch. */
  analyticsId: z.string().max(200).optional(),
});
export type FirstTouchInput = z.infer<typeof firstTouchInputSchema>;

export const signUpInputSchema = z.object({
  firstName: z.string().trim().min(1, "Please enter your first name.").max(60, "Use 60 characters or fewer."),
  lastName: z.string().trim().max(60, "Use 60 characters or fewer."),
  email: z.email("Please enter your email address."),
  password: passwordSchema,
  communications: z.boolean(),
  language: signUpLanguageSchema.optional(),
  /** A referral link's token, carried on `/register?ref=...` (S5.09); attributed once sign-up
   * succeeds, never checked here, since a bad or missing one must never refuse the sign-up. */
  referralToken: z.string().optional(),
  /** The campaign that brought this sign-up in (ADR-027 point 11, S31.09), passed on to growth. */
  firstTouch: firstTouchInputSchema.optional(),
  /** Cloudflare Turnstile's token from the form (ADR-025 §8); required while the challenge is on. */
  challengeToken: z.string().max(2048).optional(),
});
export type SignUpInput = z.infer<typeof signUpInputSchema>;

export const signedInSchema = z.object({ userId: z.string() });
/**
 * `sessionToken` never reaches the wire: the transport reads it to start the
 * session cookie, and the declared output above (just `userId`) is all a
 * caller ever sees (see `authTrpcContract`'s `signIn`/`signUp`).
 */
export type SignedIn = z.infer<typeof signedInSchema> & { readonly sessionToken?: string };

/**
 * What a sign-up answers, whether the address was new or already had an account (ADR-010
 * revision 3): check your email. `userId` is for a peer and never reaches the wire, and is
 * absent when the address was taken; nobody is signed in until the mailed link is opened.
 */
export const signedUpSchema = z.object({ checkEmail: z.literal(true) });
export type SignedUp = z.infer<typeof signedUpSchema> & { readonly userId?: string };

// Single sign-on. A company that proves it owns a domain routes everyone on
// it to its identity provider; once enforced, passwords stop working there.

export const signInMethodSchema = z.enum(["password", "sso"]);
export type SignInMethod = z.infer<typeof signInMethodSchema>;

export const signInRouteSchema = z.object({
  method: signInMethodSchema,
  /** The connection to continue with, when the domain has a live one. */
  connectionId: z.string().nullable(),
  providerName: z.string().nullable(),
  /** Passwords are off for this domain: single sign-on is the only way in. */
  enforced: z.boolean(),
});
export type SignInRoute = z.infer<typeof signInRouteSchema>;

export const domainRoutesInputSchema = z.object({
  connectionId: z.string(),
  providerName: z.string(),
  domains: z.array(z.string()),
  enforced: z.boolean(),
});
export type DomainRoutesInput = z.infer<typeof domainRoutesInputSchema>;

// Domain join (S1.07): a second, independent table sso pushes into the same way it
// pushes `domainRoutesInputSchema` above, naming the organization each domain joins
// and the role it hands out; never admin (a contract may depend only on zod and
// @amtana/module, so the role list is restated here rather than imported, D017).
export const domainJoinRoleSchema = z.enum(["member", "viewer"]);
export type DomainJoinRole = z.infer<typeof domainJoinRoleSchema>;

export const domainJoinRouteSchema = z.object({
  domain: z.string(),
  defaultRole: domainJoinRoleSchema,
});

export const domainJoinRoutesInputSchema = z.object({
  connectionId: z.string(),
  organizationId: z.string(),
  domains: z.array(domainJoinRouteSchema),
});
export type DomainJoinRoutesInput = z.infer<typeof domainJoinRoutesInputSchema>;

export const ssoAdmissionSchema = z.object({
  email: z.email("Please enter a valid email address."),
  firstName: z.string(),
  lastName: z.string(),
  /** Whether someone the platform has never seen may be created on the way in. */
  createMissing: z.boolean(),
});
export type SsoAdmission = z.infer<typeof ssoAdmissionSchema>;

export const admittedSchema = z.object({ userId: z.string(), created: z.boolean() });
/** `sessionToken` is for the caller (sso's `idpSignIn`) to start its own session with; never on the wire. */
export type Admitted = z.infer<typeof admittedSchema> & { readonly sessionToken?: string };

export const emailInputSchema = z.object({ email: z.email("Please enter a valid email address.") });
export const okSchema = z.object({ ok: z.literal(true) });
export const emptyInputSchema = z.object({});

// Verifying an email address with a mailed link (ADR-004 point 5).

export const confirmEmailVerificationInputSchema = z.object({
  token: z.string().min(1, "This verification link is missing its token."),
});
export type ConfirmEmailVerificationInput = z.infer<typeof confirmEmailVerificationInputSchema>;

export const verifiedSchema = z.object({ verified: z.literal(true) });
/** A spent verification link signs its owner in: `userId` and `sessionToken` never reach the wire. */
export type Verified = z.infer<typeof verifiedSchema> & {
  readonly userId?: string;
  readonly sessionToken?: string;
};

// Resetting a forgotten password with a mailed link (ADR-004 points 3 and 5).

export const confirmPasswordResetInputSchema = z.object({
  token: z.string().min(1, "This reset link is missing its token."),
  password: passwordSchema,
});
export type ConfirmPasswordResetInput = z.infer<typeof confirmPasswordResetInputSchema>;

// Sessions, listed and ended one by one or all at once (S0.03); changing a
// password from My account (S0.06). A listed session never carries its raw
// user agent string or an IP address to the wire: only the two short labels
// a person recognises their own device and browser by (Decided in spec).

export const sessionSummarySchema = z.object({
  /** Opaque, not a secret: the session's own token hash, never the token itself. */
  id: z.string(),
  device: z.string(),
  browser: z.string(),
  createdAt: z.string(),
  lastSeenAt: z.string(),
  /** The session the caller is asking from right now. */
  isCurrent: z.boolean(),
});
export type SessionSummary = z.infer<typeof sessionSummarySchema>;

export const sessionsListSchema = z.object({ sessions: z.array(sessionSummarySchema) });
export type SessionsList = z.infer<typeof sessionsListSchema>;

export const endSessionInputSchema = z.object({
  id: z.string().min(1, "This session no longer exists."),
});
export type EndSessionInput = z.infer<typeof endSessionInputSchema>;

export const changePasswordInputSchema = z.object({
  currentPassword: z.string().min(1, "Enter your current password."),
  newPassword: passwordSchema,
});
export type ChangePasswordInput = z.infer<typeof changePasswordInputSchema>;

// Changing the sign-in email (S6.02): the current password proves it is the
// account holder asking; nothing changes until a mailed link to the new
// address is confirmed, so sign-in keeps working with the old address until
// then (Decided in spec).

export const requestEmailChangeInputSchema = z.object({
  newEmail: z.email("Enter a valid email address."),
  currentPassword: z.string().min(1, "Enter your current password."),
});
export type RequestEmailChangeInput = z.infer<typeof requestEmailChangeInputSchema>;

export const confirmEmailChangeInputSchema = z.object({
  token: z.string().min(1, "This confirmation link is missing its token."),
});
export type ConfirmEmailChangeInput = z.infer<typeof confirmEmailChangeInputSchema>;

export const emailChangedSchema = z.object({ email: z.email() });
export type EmailChanged = z.infer<typeof emailChangedSchema>;

// A link minted only once its delivery sends (ADR-022 point 7, S26.12): the event log never
// holds a token, so worker-bridge asks for a fresh one, through its own peer (not notification's,
// see D500 of dev/docs/decisions.md), the moment a delivery is about to mail it. Minting replaces
// whatever token the same kind already held for `userId` (the newest link wins), the same as a
// plain resend always has.

export const mintEmailLinkInputSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("verification"), userId: z.string().min(1) }),
  z.object({ kind: z.literal("passwordReset"), userId: z.string().min(1) }),
  z.object({
    kind: z.literal("emailChange"),
    userId: z.string().min(1),
    newEmail: z.email(),
  }),
]);
export type MintEmailLinkInput = z.infer<typeof mintEmailLinkInputSchema>;

export const mintedEmailLinkSchema = z.object({ link: z.string().min(1) });
export type MintedEmailLink = z.infer<typeof mintedEmailLinkSchema>;

// Deleting an account (S6.08): a grace period before the purge is final, refused while the
// caller is the last admin of an organization with other members. `confirmEmail` (review group
// 3 security finding 8) is checked server-side in `ErasureGateService`, trimmed and
// case-insensitive, the same leniency a mistyped sign-in email already gets: the browser's own
// typed-confirmation UI is not itself a guard once this door is reachable directly.

export const deleteAccountInputSchema = z.object({
  confirmEmail: z.string().trim().min(1, "Type your own email to confirm."),
});
export type DeleteAccountInput = z.infer<typeof deleteAccountInputSchema>;

export const deletionStartedSchema = z.object({ purgeAt: z.iso.datetime() });
export type DeletionStarted = z.infer<typeof deletionStartedSchema>;

/** `ErasureBlockedError`'s own wire `details` (review group 3 security finding 7, revising
 * D863): the owning contract is the one place that knows this shape, so the Danger Zone reads
 * it through this schema's own `safeParse` rather than assuming an untyped object parsed off
 * the wire is shaped the way it expects. */
export const erasureBlockedDetailsSchema = z.object({
  organizations: z.array(z.object({ organizationId: z.string(), name: z.string() })),
});
export type ErasureBlockedDetails = z.infer<typeof erasureBlockedDetailsSchema>;

// Passkeys: WebAuthn (ADR-004 point 4 of dev/docs/adrs/004-sign-in-methods.md). A
// person adds one from My account and signs in with it, no password. The
// server keeps the credential id, public key, signature counter and
// transports, and checks the counter on every use. The ceremony's own JSON
// (the W3C WebAuthn options and responses, https://www.w3.org/TR/webauthn-3/)
// passes through to and from the browser almost verbatim: the schemas below
// type the fields this platform reads and pass every other one through, so
// an authenticator's own extra fields are never refused.

const passthroughRecord = z.record(z.string(), z.unknown());

export const passkeySchema = z.object({
  id: z.string(),
  label: z.string(),
  transports: z.array(z.string()),
  createdAt: z.iso.datetime(),
  lastUsedAt: z.iso.datetime().nullable(),
});
export type Passkey = z.infer<typeof passkeySchema>;

export const passkeysSchema = z.object({ passkeys: z.array(passkeySchema) });
export type Passkeys = z.infer<typeof passkeysSchema>;

export const renamePasskeyInputSchema = z.object({
  id: z.string().min(1),
  label: z.string().trim().min(1, "Give this passkey a name.").max(60, "Use 60 characters or fewer."),
});
export type RenamePasskeyInput = z.infer<typeof renamePasskeyInputSchema>;

export const removePasskeyInputSchema = z.object({ id: z.string().min(1) });

/** The back office's own read (S9.04, ADR-005 point 4): whether a password is set, how many
 * passkeys, and whether the address was admitted by an identity provider. */
export const signInMethodsSchema = z.object({
  password: z.boolean(),
  passkeys: z.number().int().nonnegative(),
  sso: z.boolean(),
});
export type SignInMethods = z.infer<typeof signInMethodsSchema>;

/** `navigator.credentials.create(...)`'s options, from `generateRegistrationOptions`. */
export const registrationOptionsSchema = z.looseObject({
  rp: z.looseObject({ id: z.string(), name: z.string() }),
  user: z.looseObject({ id: z.string(), name: z.string(), displayName: z.string() }),
  challenge: z.string(),
  pubKeyCredParams: z.array(passthroughRecord),
  excludeCredentials: z.array(passthroughRecord).optional(),
  authenticatorSelection: passthroughRecord.optional(),
  attestation: z.string().optional(),
  timeout: z.number().optional(),
  extensions: passthroughRecord.optional(),
});
export type RegistrationOptions = z.infer<typeof registrationOptionsSchema>;

/** `navigator.credentials.get(...)`'s options, from `generateAuthenticationOptions`. No
 * `allowCredentials` means any passkey the browser knows for this site may answer
 * (a discoverable credential), which is how signing in with no username works. */
export const authenticationOptionsSchema = z.looseObject({
  challenge: z.string(),
  timeout: z.number().optional(),
  rpId: z.string().optional(),
  allowCredentials: z.array(passthroughRecord).optional(),
  userVerification: z.string().optional(),
  extensions: passthroughRecord.optional(),
});
export type AuthenticationOptions = z.infer<typeof authenticationOptionsSchema>;

/** What `navigator.credentials.create(...)` returned, read back from the browser. */
export const registrationResponseInputSchema = z.looseObject({
  id: z.string(),
  rawId: z.string(),
  type: z.literal("public-key"),
  clientExtensionResults: passthroughRecord,
  authenticatorAttachment: z.string().optional(),
  response: z.looseObject({
    clientDataJSON: z.string(),
    attestationObject: z.string(),
    transports: z.array(z.string()).optional(),
  }),
});
export type RegistrationResponseInput = z.infer<typeof registrationResponseInputSchema>;

/** What `navigator.credentials.get(...)` returned, read back from the browser. */
export const authenticationResponseInputSchema = z.looseObject({
  id: z.string(),
  rawId: z.string(),
  type: z.literal("public-key"),
  clientExtensionResults: passthroughRecord,
  authenticatorAttachment: z.string().optional(),
  response: z.looseObject({
    clientDataJSON: z.string(),
    authenticatorData: z.string(),
    signature: z.string(),
    userHandle: z.string().optional(),
  }),
});
export type AuthenticationResponseInput = z.infer<typeof authenticationResponseInputSchema>;

export const addPasskeyInputSchema = z.object({ response: registrationResponseInputSchema });
export type AddPasskeyInput = z.infer<typeof addPasskeyInputSchema>;

export const passkeySignInInputSchema = z.object({ response: authenticationResponseInputSchema });
export type PasskeySignInInput = z.infer<typeof passkeySignInInputSchema>;

// Sign in with Google or LinkedIn (ADR-007, revision 2): one OpenID Connect flow, parametric in
// the provider. Starting answers the address to send the browser to; the provider sends it back
// to the platform's /auth/<provider>/callback with a code and the same `state`.

export const oidcProviderSchema = z.enum(["google", "linkedin"]);
export type OidcProvider = z.infer<typeof oidcProviderSchema>;

export const oidcSignInStartInputSchema = z.object({
  provider: oidcProviderSchema,
  /** The language the sign-in screen had active, for an account the provider creates (S6.04). */
  language: signUpLanguageSchema.optional(),
  /** The campaign that brought this visit in (ADR-027 point 11, S31.09), carried in the OIDC
   * request the same way `language` already is, for an account the provider's return creates. */
  firstTouch: firstTouchInputSchema.optional(),
});
export type OidcSignInStartInput = z.infer<typeof oidcSignInStartInputSchema>;

/** Connecting or disconnecting a provider from My account. */
export const oidcProviderInputSchema = z.object({ provider: oidcProviderSchema });
export type OidcProviderInput = z.infer<typeof oidcProviderInputSchema>;

export const oidcRedirectSchema = z.object({ url: z.url() });
export type OidcRedirect = z.infer<typeof oidcRedirectSchema>;

export const oidcSignInInputSchema = z.object({
  /** Whose callback the browser came back to; it must be the provider the sign-in started with. */
  provider: oidcProviderSchema,
  code: z.string().min(1),
  state: z.string().min(1),
});
export type OidcSignInInput = z.infer<typeof oidcSignInInputSchema>;

/** What a return from a provider did: signed someone in, or linked it to whoever started it. */
export const oidcSignedInSchema = z.object({
  userId: z.string(),
  purpose: z.enum(["sign-in", "link"]),
  created: z.boolean(),
});
/** `sessionToken`, only for a sign-in, never reaches the wire (see `SignedIn`). */
export type OidcSignedIn = z.infer<typeof oidcSignedInSchema> & { readonly sessionToken?: string };
